import React, {useState} from 'react';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import CodeIcon from '@mui/icons-material/Code';
import { getFileExtension } from '../helpers/javascriptHelpers';
import useSignedFileUrl from '../helpers/authenticatedFile';

const fileIconMap = {
    pdf: { icon: PictureAsPdfIcon, color: '#d32f2f', label: 'PDF' },
    doc: { icon: DescriptionIcon, color: '#1976d2', label: 'Word Document' },
    docx: { icon: DescriptionIcon, color: '#1976d2', label: 'Word Document' },
    rtf: { icon: DescriptionIcon, color: '#1976d2', label: 'Rich Text' },
    odt: { icon: DescriptionIcon, color: '#1976d2', label: 'OpenDocument Text' },
    xls: { icon: TableChartIcon, color: '#388e3c', label: 'Excel' },
    xlsx: { icon: TableChartIcon, color: '#388e3c', label: 'Excel' },
    csv: { icon: TableChartIcon, color: '#388e3c', label: 'CSV' },
    ppt: { icon: SlideshowIcon, color: '#e65100', label: 'PowerPoint' },
    pptx: { icon: SlideshowIcon, color: '#e65100', label: 'PowerPoint' },
    zip: { icon: FolderZipIcon, color: '#6a1b9a', label: 'Archive' },
    rar: { icon: FolderZipIcon, color: '#6a1b9a', label: 'Archive' },
    '7z': { icon: FolderZipIcon, color: '#6a1b9a', label: 'Archive' },
    tar: { icon: FolderZipIcon, color: '#6a1b9a', label: 'Archive' },
    gz: { icon: FolderZipIcon, color: '#6a1b9a', label: 'Archive' },
    mp4: { icon: VideoFileIcon, color: '#455a64', label: 'Video' },
    mov: { icon: VideoFileIcon, color: '#455a64', label: 'Video' },
    avi: { icon: VideoFileIcon, color: '#455a64', label: 'Video' },
    mkv: { icon: VideoFileIcon, color: '#455a64', label: 'Video' },
    webm: { icon: VideoFileIcon, color: '#455a64', label: 'Video' },
    mp3: { icon: AudioFileIcon, color: '#5d4037', label: 'Audio' },
    wav: { icon: AudioFileIcon, color: '#5d4037', label: 'Audio' },
    ogg: { icon: AudioFileIcon, color: '#5d4037', label: 'Audio' },
    flac: { icon: AudioFileIcon, color: '#5d4037', label: 'Audio' },
    txt: { icon: TextSnippetIcon, color: '#616161', label: 'Text' },
    md: { icon: TextSnippetIcon, color: '#616161', label: 'Markdown' },
    js: { icon: CodeIcon, color: '#616161', label: 'Code' },
    ts: { icon: CodeIcon, color: '#616161', label: 'Code' },
    html: { icon: CodeIcon, color: '#616161', label: 'Code' },
    css: { icon: CodeIcon, color: '#616161', label: 'Code' },
    json: { icon: CodeIcon, color: '#616161', label: 'Code' },
    xml: { icon: CodeIcon, color: '#616161', label: 'Code' },
};

const FileTypeIcon = ({ filePath, title }) => {
    const [failed, setFailed] = useState(false);
    // A signed URL can be opened directly, so the document opens in a new tab as
    // it always did, with no blob and no second request.
    const { url } = useSignedFileUrl(filePath);

    // Hooks run before the early return below: bailing out first would change
    // how many hooks this component uses from one render to the next.
    const ext = getFileExtension(filePath);
    const entry = fileIconMap[ext] || { icon: InsertDriveFileIcon, color: '#757575', label: 'File' };
    const Icon = entry.icon;

    if (!filePath) return null;

    // Stored documents are no longer world-readable, so a plain link would open
    // a 401 in a new tab. The bytes are fetched with the caller's token and saved
    // through the browser instead.
    const handleClick = async e => {
        e.preventDefault();
        e.stopPropagation();

        if (!url) {
            setFailed(true);
            return;
        }

        setFailed(false);
        window.open(url, '_blank', 'noopener');
    };

    return (
        <Tooltip title={failed ? 'Could not open this file' : `${title || entry.label}${ext ? ' (' + ext.toUpperCase() + ')' : ''}`}>
            <Link
                href={url || undefined}
                onClick={handleClick}
                aria-label={title || entry.label}
                sx={{ cursor: 'pointer' }}
            >
                <Icon style={{ fontSize: 48, color: failed ? '#bdbdbd' : entry.color }} />
            </Link>
        </Tooltip>
    );
};

export default FileTypeIcon;
